import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SettingsRequestError, settingsApi } from "./api";
import { parseSearchTermDraft, searchTermDraft, type AdminSearchTermDraft } from "./admin-library-form";
import type { AdminSearchTerm, AdminSearchTermInput } from "./types";
import "./admin-libraries.css";

function saveError(error: unknown): string {
  if (error instanceof SettingsRequestError && error.code === "admin_term_name_alias_duplicate") return "名称或别名已被使用，请换一个。";
  if (error instanceof SettingsRequestError && error.status === 409) return "术语已发生变化。你的草稿已保留，请核对名称和别名后重试。";
  return "搜索术语保存失败，请稍后重试。";
}

interface AdminSearchTermsSettingsProps {
  headerActionsTarget?: HTMLElement | null;
}

function sameTermDraft(draft: AdminSearchTermDraft, item: AdminSearchTerm | null): boolean {
  const baseline = searchTermDraft(item ?? undefined);
  return draft.name === baseline.name && draft.aliases === baseline.aliases && draft.description === baseline.description;
}

function sameTermValue(value: AdminSearchTermInput | undefined, item: AdminSearchTerm | null): boolean {
  return Boolean(value && item && value.name === item.name && value.description === item.description
    && value.aliases.length === item.aliases.length && value.aliases.every((alias, index) => alias === item.aliases[index]));
}

export function AdminSearchTermsSettings({ headerActionsTarget = null }: AdminSearchTermsSettingsProps) {
  const [items, setItems] = useState<AdminSearchTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState<AdminSearchTerm | null | undefined>(undefined);
  const [draft, setDraft] = useState<AdminSearchTermDraft>(() => searchTermDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [conflicted, setConflicted] = useState(false);
  const [discardNotice, setDiscardNotice] = useState(false);
  const [pendingOpen, setPendingOpen] = useState<AdminSearchTerm | null | undefined>(undefined);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  function load(): void {
    setLoading(true);
    setLoadError("");
    void settingsApi.loadAdminSearchTerms().then(setItems).catch(() => setLoadError("无法读取搜索术语，请稍后重试。"))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function open(item: AdminSearchTerm | null): void {
    setEditing(item);
    setDraft(searchTermDraft(item ?? undefined));
    setError("");
    setConflicted(false);
    setDiscardNotice(false);
    setPendingOpen(undefined);
    setRequestId(crypto.randomUUID());
  }

  function requestOpen(item: AdminSearchTerm | null): void {
    if (editing !== undefined && !sameTermDraft(draft, editing)) {
      setDiscardNotice(true);
      setPendingOpen(item);
      return;
    }
    open(item);
  }

  function cancel(): void {
    setEditing(undefined);
    setError("");
    setConflicted(false);
    setDiscardNotice(false);
    setPendingOpen(undefined);
  }

  async function refreshConflict(): Promise<void> {
    if (!editing) return;
    setSaving(true);
    try {
      const latest = (await settingsApi.loadAdminSearchTerms()).find((item) => item.id === editing.id);
      if (!latest) throw new Error("admin_term_missing_after_conflict");
      setItems((current) => current.map((item) => item.id === latest.id ? latest : item));
      setEditing(latest);
      setConflicted(false);
      setError("已载入最新版本；你的名称、别名和说明草稿仍保留，请核对后保存。");
      setRequestId(crypto.randomUUID());
    } catch { setError("无法载入最新版本，草稿仍保留，请稍后重试。"); }
    finally { setSaving(false); }
  }

  async function save(): Promise<void> {
    const parsed = parseSearchTermDraft(draft, items, editing?.id);
    if (!parsed.value) {
      setError(parsed.error ?? "请检查输入。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = editing
        ? await settingsApi.updateAdminSearchTerm(editing.id, parsed.value, editing.revision, requestId)
        : await settingsApi.createAdminSearchTerm(parsed.value, requestId);
      setItems((current) => editing ? current.map((item) => item.id === updated.id ? updated : item) : [...current, updated]);
      setEditing(undefined);
      setDiscardNotice(false);
      setPendingOpen(undefined);
    } catch (cause) {
      setError(saveError(cause));
      setConflicted(cause instanceof SettingsRequestError && cause.status === 409 && cause.code !== "admin_term_name_alias_duplicate");
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (!editing || !window.confirm(`确定删除术语“${editing.name}”吗？这会影响所有用户。`)) return;
    setSaving(true);
    setError("");
    try {
      await settingsApi.deleteAdminSearchTerm(editing.id, editing.revision);
      setItems((current) => current.filter((item) => item.id !== editing.id));
      cancel();
    } catch (cause) {
      setError(cause instanceof SettingsRequestError && cause.status === 409
        ? "术语已发生变化，未删除。请载入最新版本后重试。"
        : "删除失败，请稍后重试。");
      setConflicted(cause instanceof SettingsRequestError && cause.status === 409);
    } finally {
      setSaving(false);
    }
  }


  const parsedDraft = editing === undefined ? {} : parseSearchTermDraft(draft, items, editing?.id);
  const draftDirty = editing !== undefined && !sameTermDraft(draft, editing);
  const saveDisabled = editing === undefined || saving || !draftDirty || !parsedDraft.value || sameTermValue(parsedDraft.value, editing);
  const headerActions = <>
    <button className="panel-fab" type="button" aria-label="新建术语" title="新建术语" disabled={loading || saving || Boolean(loadError)} onClick={() => requestOpen(null)}>+</button>
    <button className="panel-fab" type="button" aria-label="保存术语" title="保存术语" disabled={saveDisabled} onClick={() => void save()}>✓</button>
  </>;

  return <>
    {headerActionsTarget ? createPortal(headerActions, headerActionsTarget) : null}
    {loading ? <p className="panel-state" role="status">正在读取搜索术语…</p> : loadError ? <div className="panel-error" role="alert"><p>{loadError}</p><button className="secondary outlined" type="button" onClick={load}>重试</button></div> : <div className="admin-library-stack">
    {editing !== undefined ? <section className="admin-term-editor" aria-labelledby="admin-term-editor-title">
      <header><div><h3 id="admin-term-editor-title">{editing ? `编辑术语：${editing.name}` : "新建搜索术语"}</h3><p>{editing ? `当前版本 ${editing.revision}` : "保存后会立即加入所有用户共享的搜索术语列表。"}</p></div><button type="button" aria-label="取消术语编辑" title="取消术语编辑" disabled={saving} onClick={cancel}>×</button></header>
      {discardNotice ? <div className="admin-term-dirty-notice" role="alert"><span>当前草稿尚未保存。请先保存，或明确取消草稿后再切换。</span><div><button className="secondary outlined" type="button" disabled={saveDisabled} onClick={() => void save()}>保存当前草稿</button><button className="secondary outlined" type="button" disabled={saving || pendingOpen === undefined} onClick={() => pendingOpen !== undefined && open(pendingOpen)}>{pendingOpen ? `取消草稿并编辑${pendingOpen.name}` : "取消草稿并新建"}</button></div></div> : null}
      <label>名称<input autoFocus value={draft.name} disabled={saving} onChange={(event) => { setDraft((current) => ({ ...current, name: event.target.value })); setDiscardNotice(false); }} /></label>
      <label>别名<textarea className="admin-alias-input" value={draft.aliases} disabled={saving} placeholder="每行一个，也可以用逗号分隔" onChange={(event) => { setDraft((current) => ({ ...current, aliases: event.target.value })); setDiscardNotice(false); }} /></label>
      <label>说明<textarea value={draft.description} disabled={saving} onChange={(event) => { setDraft((current) => ({ ...current, description: event.target.value })); setDiscardNotice(false); }} /></label>
      {error ? <p className="action-error" role="alert">{error}</p> : draftDirty && parsedDraft.error ? <p className="action-error" role="alert">{parsedDraft.error}</p> : null}
      {conflicted ? <button className="secondary outlined admin-conflict-refresh" type="button" disabled={saving} onClick={() => void refreshConflict()}>载入最新版本（保留草稿）</button> : null}
      <div className="admin-editor-actions">{editing ? <button className="secondary outlined" type="button" disabled={saving} onClick={() => void remove()}>删除</button> : null}<button className="secondary outlined" type="button" disabled={saving} onClick={cancel}>取消</button></div>
    </section> : <p className="admin-term-hint">这是所有已登录用户共同维护的全局术语列表。名称和别名不能重复；选择一条术语进行编辑，或使用标题右侧的新建按钮。</p>}
    {items.length === 0 ? <p className="panel-state">暂无搜索术语。</p> : <div className="admin-term-table">
      <div className="admin-term-heading"><span>名称 / 别名</span><span>说明</span><span /></div>
      {items.map((item) => <article className="admin-term-row" key={item.id}>
        <div><strong>{item.name}</strong>{item.aliases.length ? <small>{item.aliases.join("、")}</small> : <small>无别名</small>}</div>
        <p>{item.description}</p>
        <button className="secondary outlined" type="button" disabled={saving} onClick={() => requestOpen(item)}>编辑</button>
      </article>)}
    </div>}
  </div>}
  </>;
}
